import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { IconPencil } from 'components/atoms/icons/IconPencil';
import {
  DECIMAL_POINT,
  DELETE_KEY,
  INITIAL_VALUE,
  NumberPad,
} from '../NumberPad';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IconCheckCircle } from 'components/atoms/icons/IconCheckCircle';
import { TenantRegistrationConfirmModal } from '../CollectTenantRegistrationConfirmModal';
import { Modal } from 'components/atoms/Modal';
import { WasteUnitItem } from 'types/routeManagementTypes';
import { CollectItem } from './CollectItem';
import { useParams } from 'react-router-dom';
import { createCollectSubstituteRegist } from 'apis/collects';
import { toast } from 'react-toastify';
import { nonNull, normalizeNumber } from 'util/commons';
import { LAYOUT_BREAK_POINTS, REGEX_OBJECT } from 'util/ConstantValues';
import BigNumber from 'bignumber.js';
import Popover from 'components/atoms/Popover';
import { useGetWindowSize } from 'util/hooks/useGetWindowSize';
import { ReportAbnormalityModal } from '../ReportAbnormalityModal';
import Portal from 'components/atoms/Portal';
import { ItemType, Tenant } from 'util/Types';
import CollectEmissionsSelectDriverModal from '../CollectEmissionsSelectDriverModal';
import { RoleType } from 'util/Enums';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { isRouteIdExist } from 'util/collectingRoute';

export const TenantRegistrationForm = ({
  wasteUnits: rawWasteUnits,
  tenant,
  siteName,
  submitSuccess,
}: {
  wasteUnits: WasteUnitItem[];
  tenant?: Tenant;
  siteName: string;
  submitSuccess?: () => void;
}) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);

  const { routeId, siteId } = useParams() as {
    routeId: string;
    siteId: string;
  };

  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [items, setItems] = useState<WasteUnitItem[]>([]);
  const [editingItem, setEditingItem] = useState<WasteUnitItem>();
  const [checkedMap, setCheckedMap] = useState<Record<number, boolean>>({});
  const numberPadRef = useRef<HTMLDivElement>(null);
  const itemListRef = useRef<HTMLDivElement>(null);
  const { windowWidth } = useGetWindowSize();
  const [isShowDriverModal, setIsShowDriverModal] = useState<boolean>(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');

  const [reportAbnormalityModal, setReportAbnormalityModal] = useState<
    | {
        name: string;
        weight: string;
        wasteUnitId: number;
      }
    | undefined
  >();

  const isMobile = useMemo(
    () => windowWidth <= LAYOUT_BREAK_POINTS.MD,
    [windowWidth]
  );

  const wasteUnits = useMemo(() => {
    if (!tenant) {
      return rawWasteUnits;
    }

    return rawWasteUnits.filter((unit) =>
      tenant.tenantItems?.some((id) => id === unit.id)
    );
  }, [rawWasteUnits, tenant]);

  useEffect(() => {
    setItems(wasteUnits.map((unit) => ({ ...unit, weight: '0' })));
  }, [wasteUnits]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const isClickInsideNumberPad = numberPadRef.current?.contains(target);
      const isClickInsideItemList = itemListRef.current?.contains(target);

      if (!isClickInsideNumberPad && !isClickInsideItemList && !isMobile) {
        setEditingItem(undefined);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, editingItem]);

  useEffect(() => {
    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && editingItem) {
        event.preventDefault();

        const currentIndex = items.findIndex(
          (item) => item.id === editingItem.id
        );
        if (currentIndex === -1) return;

        const nextIndex = (currentIndex + 1) % items.length;
        const nextItem = items[nextIndex];

        setEditingItem(nextItem);
      }
    };

    document.addEventListener('keydown', handleTabKey);

    return () => document.removeEventListener('keydown', handleTabKey);
  }, [editingItem, items]);

  const checkedItems = useMemo(
    () => items.filter((item) => checkedMap[item.id]),
    [items, checkedMap]
  );

  const totalRegisteringWeight = useMemo(() => {
    return checkedItems
      .reduce(
        (total, item) =>
          BigNumber(item.weight || 0)
            .multipliedBy(item.convertKg || 1)
            .plus(total),
        BigNumber(0)
      )
      .toNumber();
  }, [checkedItems]);

  const calculateNewWeight = (
    currentWeight: string,
    inputValue: number | string,
    isFixedItem = false
  ): string => {
    const inputString = String(inputValue);

    if (inputValue === DELETE_KEY) {
      const valueAfterDelete = currentWeight.slice(0, -1);
      const isEmpty = valueAfterDelete === '' || valueAfterDelete === '-';
      const newVal = isEmpty ? INITIAL_VALUE : valueAfterDelete;

      setCheckedMap((prev) => ({
        ...prev,
        [nonNull(editingItem?.id)]: Number(newVal) !== 0,
      }));

      return newVal;
    }

    if (inputValue === DECIMAL_POINT) {
      if (isFixedItem) {
        return currentWeight;
      }

      const hasDecimalPoint = currentWeight.includes(DECIMAL_POINT);
      const newVal = hasDecimalPoint
        ? currentWeight
        : currentWeight + DECIMAL_POINT;

      setCheckedMap((prev) => ({
        ...prev,
        [nonNull(editingItem?.id)]: Number(newVal) !== 0,
      }));

      return newVal;
    }

    const isInitialZero = currentWeight === INITIAL_VALUE;
    const isInputZero = inputString === INITIAL_VALUE;

    if (isInitialZero && isInputZero) {
      setCheckedMap((prev) => ({ ...prev, [nonNull(editingItem?.id)]: false }));

      return INITIAL_VALUE;
    }

    let newWeight = '';
    if (isInitialZero) {
      newWeight = inputString;
    } else {
      newWeight = currentWeight + inputString;
    }

    if (!REGEX_OBJECT.fiveDigitsWith2Decimal.test(newWeight)) {
      return currentWeight;
    }

    setCheckedMap((prev) => ({
      ...prev,
      [nonNull(editingItem?.id)]: Number(newWeight) !== 0,
    }));

    return newWeight;
  };

  const handleValueChange = (value: number | string) => {
    if (editingItem == null) return;

    const isFixedItem = editingItem.type === 'Fixed';

    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id !== editingItem.id) return item;

        const newWeight = calculateNewWeight(
          String(item.weight),
          value,
          isFixedItem
        );

        return {
          ...item,
          weight: newWeight,
        };
      })
    );

    setEditingItem((prevSelectedItem) => {
      if (prevSelectedItem == null) return undefined;

      const newWeight = calculateNewWeight(
        String(prevSelectedItem.weight),
        value,
        isFixedItem
      );

      return {
        ...prevSelectedItem,
        weight: newWeight,
      };
    });
  };

  const handleSubmit = () => {
    createCollectSubstituteRegist({
      body: {
        routeId: isRouteIdExist(routeId) ? Number(routeId) : undefined,
        siteId,
        tenantId: tenant?.id,
        driverId: selectedDriverId || undefined,
        waste: checkedItems.map((item) => ({
          wasteUnitId: item.id,
          quantity: item.weight || '',
        })),
      },
      successCallback: () => {
        setIsShowModalConfirm(false);
        handleCloseDriverModal();
        toast.success(t('messages.M_109'));
        setItems(wasteUnits.map((unit) => ({ ...unit, weight: '0' })));

        submitSuccess?.();
      },
    });
  };

  const handleDriverSubmit = (driverId: string) => {
    setSelectedDriverId(driverId);
    setIsShowModalConfirm(true);
  };

  const handleCloseDriverModal = () => {
    setSelectedDriverId('');
    setIsShowDriverModal(false);
  };

  return (
    <>
      <div className={styles.tenantRegistrationContainer}>
        <div className={styles.title}>
          <IconPencil size={28} color="#dc910e" />
          <h2>{t('tenant_registration.title')}</h2>
        </div>
        <div className={styles.formContent}>
          <div
            className={styles.itemList}
            ref={itemListRef}
            onClick={(e) => {
              if (e.target === e.currentTarget && editingItem) {
                setEditingItem(undefined);
              }
            }}
          >
            {items.length > 0 ? (
              items.map((item) => (
                <CollectItem
                  key={item.id}
                  item={item}
                  isEditing={item.id === editingItem?.id}
                  checked={!!checkedMap[item.id]}
                  onFocus={(item) => {
                    setEditingItem(item);
                  }}
                  onCheckedChange={(item) =>
                    setCheckedMap((prev) => ({
                      ...prev,
                      [item.id]: !prev[item.id],
                    }))
                  }
                  onNoticeClick={() => {
                    setReportAbnormalityModal({
                      name: item.name,
                      weight: String(
                        Number(item.weight) *
                          (item.type === ItemType.FIX
                            ? Number(item.convertKg)
                            : 1)
                      ),
                      wasteUnitId: item.id,
                    });
                  }}
                />
              ))
            ) : (
              <p className={styles.noItemsText}>{t('messages.M_008')}</p>
            )}
          </div>
          {!isMobile && (
            <div className={styles.inputPanel} ref={numberPadRef}>
              <h4 className={styles.label}>
                {t('tenant_registration.input_panel')}
              </h4>
              <NumberPad
                onValueChange={handleValueChange}
                disabled={!editingItem}
              />
            </div>
          )}
        </div>

        <div className={styles.actionBar}>
          <div className={styles.leftSide}>
            <p className={styles.totalLabel}>
              {t('tenant_registration.total_registered_quantity')}
            </p>
            <div className={styles.totalValueWrapper}>
              <Popover
                content={normalizeNumber({ value: totalRegisteringWeight })}
                autoGen={{
                  odd: 10,
                }}
                positions={['top']}
              >
                <span className={styles.totalValue}>
                  {normalizeNumber({
                    value: totalRegisteringWeight,
                  })}
                </span>
              </Popover>
              <span className={styles.unit}>kg</span>
            </div>
          </div>
          <div className={styles.rightSide}>
            <button
              disabled={!checkedItems.length}
              onClick={() => {
                if (checkedItems.length) {
                  [RoleType.AdminSite, RoleType.AdminOperator].includes(
                    user.role as RoleType
                  )
                    ? setIsShowDriverModal(true)
                    : setIsShowModalConfirm(true);
                }
              }}
            >
              <IconCheckCircle size={24} />
              {t('tenant_registration.confirm_registration')}
            </button>
          </div>
        </div>
      </div>

      {isShowDriverModal && (
        <CollectEmissionsSelectDriverModal
          closeModal={handleCloseDriverModal}
          onSubmit={handleDriverSubmit}
          routeId={routeId}
        />
      )}
      {isShowModalConfirm && (
        <TenantRegistrationConfirmModal
          onClose={() => setIsShowModalConfirm(false)}
          registeredItems={checkedItems}
          totalRegisteredWeight={totalRegisteringWeight}
          tenantName={tenant?.name}
          handleSubmit={handleSubmit}
        />
      )}
      {isMobile && editingItem?.id != null && (
        <div className={styles.numberPadOverlay}>
          <Modal>
            <div className={styles.numberPadModal}>
              <div className={styles.modalHeader}>
                <h4 className={styles.label}>
                  {t('tenant_registration.input_panel')}
                </h4>
              </div>
              <div className={styles.modalBody}>
                {editingItem && (
                  <CollectItem
                    item={editingItem}
                    isEditing
                    className={styles.selectedMobileItem}
                  />
                )}
                <NumberPad
                  onValueChange={handleValueChange}
                  disabled={!editingItem}
                />
                <button
                  className={styles.submitButton}
                  onClick={() => {
                    setEditingItem(undefined);
                  }}
                >
                  {t('common.button.ok')}
                </button>
              </div>
            </div>
          </Modal>
        </div>
      )}

      {reportAbnormalityModal && (
        <Portal>
          <ReportAbnormalityModal
            area={{ name: siteName, id: siteId }}
            weight={reportAbnormalityModal.weight}
            wasteUnitId={reportAbnormalityModal.wasteUnitId}
            onclose={() => setReportAbnormalityModal(undefined)}
          />
        </Portal>
      )}
    </>
  );
};
